import { useEffect, useState } from 'react';
import MainMenuCard from './MainMenuCard';
import { hasAccess } from '../../lib/modules';
import useOnlineStatus from '../../hooks/useOnlineStatus';

const HERO_IMG =
  'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_260/v1763331474/ChatGPT_Image_16_nov_2025_03_06_01_p.m._c7qlpk.png';

const ICON_PATHS = {
  pos: ['M3 3h2l1.6 9.2a2 2 0 0 0 2 1.7h7.8a2 2 0 0 0 2-1.6L20 6H6', 'M9 20h.01M17 20h.01'],
  cashDrawers: ['M4 7h16v11H4zM7 7V4h10v3', 'M8 12h4m5 0h.01'],
  closeCashRegister: ['M6 10h12v10H6zM9 10V7a3 3 0 0 1 6 0v3', 'M12 14v2'],
  waiter: ['M6 3h12v18H6zM9 7h6M9 11h6M9 15h3'], kitchen: ['M4 13h16M6 13a6 6 0 0 1 12 0M12 7V4M4 20h16'],
  products: ['M4 7l8-4 8 4-8 4zM4 7v10l8 4 8-4V7M12 11v10'], inventory: ['M4 19V9M10 19V5M16 19v-7M22 19H2'],
  incoming: ['M12 3v12m-4-4 4 4 4-4M5 20h14'], outgoing: ['M12 21V9m-4 4 4-4 4 4M5 4h14'],
  reports: ['M4 19V5M4 19h16M8 15l3-4 3 2 5-7'], expenses: ['M12 2v20M17 6.5C16 5.5 14.5 5 12.5 5 10 5 8 6.2 8 8s2 2.6 4.5 3S17 12.2 17 15s-2 4-5 4c-2 0-4-.7-5-2'],
  payroll: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.9'],
  users: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4 21a8 8 0 0 1 16 0'], customers: ['M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6M16 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6M2 20a6 6 0 0 1 12 0M10 20a6 6 0 0 1 12 0'],
  audit: ['M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M16 16l5 5M8 11l2 2 4-5'], financialPanel: ['M3 6h18v12H3zM7 10h.01M17 14h.01M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6'],
  settings: ['M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M12 2v3M12 19v3M4.9 4.9L7 7M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1L7 17M17 7l2.1-2.1'],
  globalNotice: ['M3 11v2h4l7 4V7l-7 4H3zM18 9a4 4 0 0 1 0 6M7 13l1 7h3'], suppliers: ['M3 6h11v11H3zM14 10h4l3 3v4h-7zM7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4M17 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4'],
};

const MOBILE_IMAGE_NAMES = {
  pos: 'pos', cashDrawers: 'cash-drawers', closeCashRegister: 'close-cash-register', waiter: 'waiter', kitchen: 'kitchen',
  products: 'products', inventory: 'inventory', incoming: 'incoming', outgoing: 'outgoing', reports: 'reports',
  expenses: 'expenses', payroll: 'payroll', users: 'users', customers: 'customers', audit: 'audit',
  financialPanel: 'financial-panel', settings: 'settings', globalNotice: 'global-notice', suppliers: 'suppliers',
};

const CATEGORY_LABELS = {
  operacion: 'Operación', inventario: 'Inventario', finanzas: 'Finanzas',
  analitica: 'Reportes', crm: 'Clientes', config: 'Administración',
};

const ModuleIcon = ({ view, className = 'h-6 w-6' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {(ICON_PATHS[view] || ICON_PATHS.products).map((path, index) => <path key={index} d={path} />)}
  </svg>
);

const MAX_FAVORITES = 8;
const MAX_RECENTS = 6;

const MainMenuGrid = ({ onSelectView, currentUser, onLogout }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('todas');
  const [favoriteViews, setFavoriteViews] = useState([]);
  const [recentViews, setRecentViews] = useState([]);
  const isOnline = useOnlineStatus();

  const userStorageId = String(currentUser?.uid || currentUser?.email || currentUser?.username || 'usuario')
    .trim()
    .toLowerCase();
  const favoritesKey = `valenpos:menu:favorites:${userStorageId}`;
  const recentsKey = `valenpos:menu:recents:${userStorageId}`;

  useEffect(() => {
    try {
      const favorites = JSON.parse(localStorage.getItem(favoritesKey) || '[]');
      const recents = JSON.parse(localStorage.getItem(recentsKey) || '[]');
      setFavoriteViews(Array.isArray(favorites) ? favorites.slice(0, MAX_FAVORITES) : []);
      setRecentViews(Array.isArray(recents) ? recents.slice(0, MAX_RECENTS) : []);
    } catch (_error) {
      setFavoriteViews([]);
      setRecentViews([]);
    }
  }, [favoritesKey, recentsKey]);

  const menuItems = [
    {
      title: 'Punto de Venta',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763333523/ChatGPT_Image_16_nov_2025_05_52_19_p.m._jnhphx.png',
      view: 'pos',
      category: 'operacion',
    },
    {
      title: 'Turnos de Caja (Admin)',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto/v1763333854/ChatGPT_Image_16_nov_2025_05_57_56_p.m._rvdiqz.png',
      view: 'cashDrawers',
      category: 'operacion',
    },
    {
      title: 'Cierre de Turno',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763334894/ChatGPT_Image_16_nov_2025_06_14_41_p.m._ktgewr.png',
      view: 'closeCashRegister',
      category: 'operacion',
    },
    {
      title: 'Toma de Pedidos',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763336289/ChatGPT_Image_16_nov_2025_06_38_17_p.m._ay8kkw.png',
      view: 'waiter',
      category: 'operacion',
    },
    {
      title: 'Panel de Despacho',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763336892/ChatGPT_Image_16_nov_2025_06_48_33_p.m._woflgc.png',
      view: 'kitchen',
      category: 'operacion',
    },
    {
      title: 'Productos',
      image:
        'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      view: 'products',
      category: 'inventario',
    },
    {
      title: 'Inventarios',
      image:
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      view: 'inventory',
      category: 'inventario',
    },
    {
      title: 'Entradas y Compras',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763337476/ChatGPT_Image_16_nov_2025_06_57_58_p.m._r74a2d.png',
      view: 'incoming',
      category: 'inventario',
    },
    {
      title: 'Salidas',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763338746/ChatGPT_Image_16_nov_2025_07_19_28_p.m._zoa39b.png',
      view: 'outgoing',
      category: 'inventario',
    },
    {
      title: 'Reportes de Ventas',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763339598/ChatGPT_Image_16_nov_2025_07_33_38_p.m._bxx3fw.png',
      view: 'reports',
      category: 'analitica',
    },
    {
      title: 'Gastos',
      image:
        'https://images.unsplash.com/photo-1553729459-efe14ef6055d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      view: 'expenses',
      category: 'finanzas',
    },
    {
      title: 'Nómina',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763353203/ChatGPT_Image_16_nov_2025_11_18_57_p.m._rdshyl.png',
      view: 'payroll',
      category: 'finanzas',
    },
    {
      title: 'Usuarios',
      image:
        'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      view: 'users',
      category: 'config',
    },
    {
      title: 'Clientes',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763353627/ChatGPT_Image_16_nov_2025_11_26_59_p.m._hakcbr.png',
      view: 'customers',
      category: 'crm',
    },
    {
      title: 'Auditoría',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763354116/ChatGPT_Image_16_nov_2025_11_35_09_p.m._dwi7gj.png',
      view: 'audit',
      category: 'analitica',
    },
    {
      title: 'Panel Financiero',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763354459/ChatGPT_Image_16_nov_2025_11_40_52_p.m._lfmki7.png',
      view: 'financialPanel',
      category: 'finanzas',
    },
    {
      title: 'Configuración',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763354691/ChatGPT_Image_16_nov_2025_11_44_45_p.m._obrayf.png',
      view: 'settings',
      category: 'config',
    },
    {
      title: 'Aviso Global',
      image:
        'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=800&q=80',
      view: 'globalNotice',
      category: 'config',
    },
    {
      title: 'Proveedores',
      image:
        'https://res.cloudinary.com/dbxgpws0g/image/upload/f_auto,q_auto,w_800/v1763354981/ChatGPT_Image_16_nov_2025_11_49_34_p.m._ko7rrl.png',
      view: 'suppliers',
      category: 'inventario',
    },
  ];

  const accessibleItems = menuItems.filter((item) => hasAccess(currentUser, item.view, 'Lectura'));

  const filteredItems = accessibleItems.filter((item) => {
    if (activeCategory !== 'todas' && item.category !== activeCategory) return false;
    if (!searchTerm.trim()) return true;
    return item.title.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const favoriteItems = favoriteViews
    .map((view) => accessibleItems.find((item) => item.view === view))
    .filter(Boolean);
  const recentItems = recentViews
    .filter((view) => !favoriteViews.includes(view))
    .map((view) => accessibleItems.find((item) => item.view === view))
    .filter(Boolean)
    .slice(0, 3);

  const toggleFavorite = (view) => {
    setFavoriteViews((previous) => {
      const next = previous.includes(view)
        ? previous.filter((item) => item !== view)
        : [view, ...previous].slice(0, MAX_FAVORITES);
      try { localStorage.setItem(favoritesKey, JSON.stringify(next)); } catch (_error) {}
      return next;
    });
  };

  const openModule = (view) => {
    setRecentViews((previous) => {
      const next = [view, ...previous.filter((item) => item !== view)].slice(0, MAX_RECENTS);
      try { localStorage.setItem(recentsKey, JSON.stringify(next)); } catch (_error) {}
      return next;
    });
    onSelectView(view);
  };

  const categories = [
    { key: 'todas', label: 'Todas' },
    { key: 'operacion', label: 'Operación' },
    { key: 'inventario', label: 'Inventario' },
    { key: 'finanzas', label: 'Finanzas' },
    { key: 'analitica', label: 'Reportes' },
    { key: 'crm', label: 'Clientes' },
    { key: 'config', label: 'Admin' },
  ];

  const quickActions = [
    { key: 'pos', label: 'POS', view: 'pos' },
    { key: 'waiter', label: 'Pedidos', view: 'waiter' },
    { key: 'closeCashRegister', label: 'Cierre', view: 'closeCashRegister' },
  ].filter(({ view }) => hasAccess(currentUser, view, 'Lectura'));

  const displayName = currentUser?.username || currentUser?.email || 'Usuario';
  const modulesLabel = `${filteredItems.length} módulo${filteredItems.length === 1 ? '' : 's'} disponibles`;

  const renderMobileTile = (item, { compact = false } = {}) => {
    const favorite = favoriteViews.includes(item.view);
    const mobileImage = `/menu-modules/optimized/${MOBILE_IMAGE_NAMES[item.view] || 'products'}.png`;
    return (
      <article key={item.view} className={`relative overflow-hidden rounded-[20px] border border-slate-200/80 bg-white shadow-[0_12px_28px_-22px_rgba(15,23,42,0.8)] transition active:scale-[0.98] ${compact ? 'min-w-[148px]' : ''}`}>
        <button
          type="button"
          onClick={() => openModule(item.view)}
          className="flex min-h-[104px] w-full items-center gap-2 rounded-[20px] py-3 pl-2 pr-7 text-left"
        >
          <span aria-hidden="true" className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/60">
            <img src={mobileImage} alt="" loading="lazy" decoding="async" className="h-[70px] w-[70px] object-contain" />
          </span>
          <span className="min-w-0">
            <span className="line-clamp-2 block text-[11px] font-extrabold leading-[1.2] text-slate-900">{item.title}</span>
            <span className="mt-1 block text-[9px] font-semibold uppercase tracking-wide text-slate-400">{CATEGORY_LABELS[item.category]}</span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => toggleFavorite(item.view)}
          aria-label={favorite ? `Quitar ${item.title} de favoritos` : `Agregar ${item.title} a favoritos`}
          title={favorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
          className={`absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full text-xs ${favorite ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-300'}`}
        >
          {favorite ? '★' : '☆'}
        </button>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#eff6ff_0,_#f8fafc_34%,_#f8fafc_100%)] px-2 py-3 pb-8 text-slate-900 sm:px-4 md:px-8 md:py-6">
      <div className="mx-auto w-full max-w-5xl space-y-6 md:space-y-8">
        <header className="sticky top-0 z-40 overflow-hidden rounded-[22px] border border-white/80 bg-white/90 p-4 shadow-[0_14px_40px_-24px_rgba(15,23,42,0.55)] backdrop-blur-xl md:static md:p-6">
          <span aria-hidden="true" className="absolute -right-12 -top-20 h-44 w-44 rounded-full bg-blue-100/70 blur-2xl" />
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <img
                src={HERO_IMG}
                alt="ValenPOS"
                className="h-12 w-12 md:h-14 md:w-14 rounded-2xl object-cover shadow-sm"
                loading="lazy"
                decoding="async"
              />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
                  Hola {displayName}
                </p>
                <h1 className="text-2xl font-bold md:text-3xl">ValenPOS</h1>
                <p className="text-xs text-slate-500 md:text-sm">Todo tu negocio en un panel rápido</p>
              </div>
            </div>

            <div className="flex w-full items-center gap-2 md:w-auto">
              <input
                type="search"
                placeholder="Buscar módulo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="relative min-w-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50/90 px-3 py-3 text-sm shadow-inner focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 md:w-72 md:px-4"
              />
              <button
                onClick={onLogout}
                disabled={!isOnline}
                title={
                  isOnline
                    ? 'Cerrar sesión'
                    : 'Recupera la conexión a Internet para cerrar sesión.'
                }
                className={`inline-flex items-center justify-center rounded-xl border border-transparent px-4 py-2.5 text-sm font-semibold shadow-sm md:px-5 ${
                  isOnline
                    ? 'bg-slate-900 text-white hover:bg-slate-800'
                    : 'cursor-not-allowed bg-slate-200 text-slate-500'
                }`}
              >
                <span className="md:hidden">Salir</span>
                <span className="hidden md:inline">Cerrar sesión</span>
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide transition ${
                  activeCategory === cat.key
                    ? 'border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-200'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </header>

        <main className="space-y-5 md:hidden">
          {quickActions.length > 0 && (
            <section>
              <div className="mb-2 flex items-center justify-between px-1">
                <h2 className="text-sm font-black text-slate-900">Accesos rápidos</h2>
                <span className="text-[11px] text-slate-400">Operación diaria</span>
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                {quickActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    onClick={() => openModule(action.view)}
                    className="flex min-h-[82px] flex-col items-center justify-center gap-1.5 rounded-[20px] bg-gradient-to-br from-slate-800 to-slate-950 px-2 py-3 text-white shadow-lg shadow-slate-300 transition active:scale-[0.97]"
                  >
                    <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/10 text-blue-200"><ModuleIcon view={action.view} className="h-5 w-5" /></span>
                    <span className="text-xs font-bold">{action.label}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {(favoriteItems.length > 0 || recentItems.length > 0) && (
            <section>
              <div className="mb-2 flex items-center justify-between px-1">
                <h2 className="text-sm font-black text-slate-900">{favoriteItems.length > 0 ? 'Favoritos' : 'Usados recientemente'}</h2>
                <span className="text-[11px] text-slate-400">Toca ☆ para personalizar</span>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {(favoriteItems.length > 0 ? favoriteItems : recentItems).slice(0, 6).map((item) => renderMobileTile(item, { compact: true }))}
              </div>
            </section>
          )}

          <section className="rounded-[24px] border border-white bg-white/80 p-3 shadow-[0_18px_45px_-28px_rgba(15,23,42,0.55)] backdrop-blur">
            <div className="mb-3 flex items-center justify-between gap-3 px-1">
              <h2 className="text-base font-black">Todos los módulos</h2>
              <p className="text-[11px] text-slate-500">{modulesLabel}</p>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {filteredItems.map((item) => renderMobileTile(item))}
            </div>
            {filteredItems.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center">
                <p className="font-bold text-slate-700">No encontramos módulos</p>
                <p className="mt-1 text-xs text-slate-500">Prueba otra categoría o búsqueda.</p>
              </div>
            )}
          </section>
        </main>

        <section className="hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:block md:p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold md:text-xl">Tus accesos</h2>
            <p className="text-xs text-slate-500 md:text-sm">{modulesLabel}</p>
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3 sm:gap-4">
            {filteredItems.map((item, index) => (
              <MainMenuCard
                key={`${item.view}-${index}`}
                title={item.title}
                image={item.image}
                onClick={() => openModule(item.view)}
              />
            ))}

            {filteredItems.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 px-6 py-10 text-center">
                <p className="text-lg font-semibold text-slate-700">No encontramos resultados</p>
                <p className="mt-1 text-sm text-slate-500">Ajusta el filtro o la búsqueda para ver más módulos.</p>
              </div>
            )}
          </div>
        </section>

        <section className="hidden rounded-2xl border border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50 p-5 text-sm text-slate-600 shadow-inner md:block">
          <p>© 2025 Valencode System. Todos los derechos reservados.</p>
          <p className="text-xs text-slate-500">
            Optimizado para pantallas táctiles: desliza los filtros o toca cualquier tarjeta para abrir un módulo.
          </p>
        </section>
      </div>

    </div>
  );
};

export default MainMenuGrid;
